import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconArrowRight } from '@tabler/icons-react';
import { currentProjects, previousProjects, siftedFeaturedProject, instrumentalProject } from '../data/projects';
import ParticlesBackground from '../components/ParticlesBackground';
import './Home.css';

const Home: React.FC = () => {
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0 });
  const [followingCta, setFollowingCta] = useState({ show: false, x: 0, y: 0, text: 'Learn more' });

  // Set document title for home page
  useEffect(() => {
    document.title = 'Zi Wang\'s portfolio';
  }, []);

  const handleMouseEnter = (e: React.MouseEvent) => {
    setTooltip({ show: true, x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0 });
  };

  const handleProjectMouseEnter = (e: React.MouseEvent, text: string = 'Learn more') => {
    setFollowingCta({ show: true, x: e.clientX, y: e.clientY, text });
  };

  const handleProjectMouseMove = (e: React.MouseEvent) => {
    setFollowingCta(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleProjectMouseLeave = () => {
    setFollowingCta({ show: false, x: 0, y: 0, text: 'Learn more' });
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section id="hero" className="hero">
        <ParticlesBackground />
        <div className="grid-container" 
             onMouseMove={(e) => {
               const heroSection = e.currentTarget.closest('.hero');
               const rect = heroSection?.getBoundingClientRect();
               if (rect) {
                 const event = new CustomEvent('heroMouseMove', {
                   detail: { x: e.clientX - rect.left, y: e.clientY - rect.top }
                 });
                 window.dispatchEvent(event);
               }
             }}
             onMouseLeave={() => {
               const event = new CustomEvent('heroMouseLeave');
               window.dispatchEvent(event);
             }}>
          <div className="grid-column grid-span-6">
            <p className="hero-description">
              Hi I'm Zi Wang—
              <br />
              a designer moved by logic, empathy, and the spaces in between.
              <br /><br />
              This is my monologue.
              <br /><br />
              <span className="hero-serif-text">
                Grew up in Wuhan,
                <br />
                drifted through Singapore,
                <br />
                and landed in California—
                <br />
                mildly sunburned, mostly grateful.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Current Work Title */}
      <section id="current-work" className="section-title-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">Highlight</h2>
          </div>
        </div>
      </section>

      {/* Current Work Section */}
      <section className="section current-work-section">
        <div className="grid-container">
          <div className="grid-column grid-span-2 description-column">
            <div className="section-description">
              <h4><strong>Instrumental – AI-Powered Manufacturing Intelligence</strong></h4>
              <p> Redesigned a key investigation tool so engineers could actually use it every day — turning an overlooked feature into their core workflows.</p>
              <p><strong>Impact:</strong></p>
              <ul>
                <li>Launched the Solve AI feature in Q4 2024, with 4/5 active customer orgs adopting it within the first month of beta</li>
                <li>Led the creation and rollout of Instrumental’s first component-based design system with engineering, improving delivery speed and UI consistency across the platform</li>

              </ul>
            </div>
          </div>
          
          <div className="grid-column grid-span-4">
            {/* Featured Project - Instrumental */}
            <div className="featured-project">
              <div className="featured-project-card">
                <Link 
                  to="/project/instrumental" 
                  key={instrumentalProject.id} 
                  className="featured-project-image"
                  onMouseEnter={handleProjectMouseEnter}
                  onMouseMove={handleProjectMouseMove}
                  onMouseLeave={handleProjectMouseLeave}
                >
                  <img src={instrumentalProject.image} alt={instrumentalProject.title} />
                </Link>
                <div className="featured-project-content">
                  <div className="featured-project-text">
                    <p className="featured-project-description"> AI-powered correlation view linking manufacturing test data with visual evidence from the assembly line.</p>
                    <div className="project-keywords">
                      {instrumentalProject.technologies.join(' · ')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Current Projects */}
          {currentProjects.filter(project => !project.isFeatured && !['unit-filter', 'framework-transitioning'].includes(project.id)).map((project) => (
            <div key={project.id} className="grid-column grid-span-6">
              <div className="project-card">
                <Link 
                  to={`/project/${project.id}`} 
                  className="project-image"
                  onMouseEnter={handleProjectMouseEnter}
                  onMouseMove={handleProjectMouseMove}
                  onMouseLeave={handleProjectMouseLeave}
                >
                  <img src={project.image} alt={project.title} />
                </Link>
                <div className="project-content">
                  <div className="project-text">
                    <p className="project-description">{project.description}</p>
                    <div className="project-keywords">
                      {project.technologies.join(' · ')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Previous Work Title */}
      <section id="previous-work" className="section-title-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">Selected projects</h2>
          </div>
        </div>
      </section>

      {/* Previous Work Section */}
      <section className="section previous-work-section">
        <div className="grid-container">
          <div className="grid-column grid-span-2 description-column">
            <div className="section-description">
              <h4><strong>Momentum Design Lab – Data-Driven Enterprise UX</strong></h4>
              <p> Designed high-performance data visualization and operations tools for teams in logistics, agriculture, emergency response, and government — helping experts make sense of messy data and act on it with confidence.</p>
              <p><strong>Impact:</strong></p>
              <ul>
                <li>Delivered award-winning interfaces used by enterprise and public-sector teams</li>
                <li>Turned dense operational data into clear, decision-ready workflows</li>
                <li>Shipped products that improved efficiency, and helped teams make better decisions</li>
              </ul>
            </div>
          </div>
          
          <div className="grid-column grid-span-4" style={{padding: '0'}}>
            <div className="nested-grid">
              {/* Featured Previous Project - SiftedAI (Full width of nested grid) */}
              <div className="nested-grid-full">
                <div className="featured-project">
                  <div className="featured-project-card">
                    <Link 
                      to="/project/sifted-featured"
                      key={siftedFeaturedProject.id} 
                      className="featured-project-image"
                      onMouseEnter={handleProjectMouseEnter}
                      onMouseMove={handleProjectMouseMove}
                      onMouseLeave={handleProjectMouseLeave}
                    >
                      <img 
                        src={siftedFeaturedProject.image} 
                        alt={siftedFeaturedProject.title}
                        className="project-image-primary"
                      />
                      <img 
                        src="/img/sifted-2.jpg"
                        alt={siftedFeaturedProject.title}
                        className="project-image-hover"
                      />
                      {siftedFeaturedProject.award && (
                        <a 
                          href="https://ifdesign.com/en/winner-ranking/project/siftedai-a-logistics-intelligence-software/698823" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="award-badge"
                        >
                          <img src="/logos/if design award.jpg" alt={siftedFeaturedProject.award} />
                        </a>
                      )}
                    </Link>
                    <div className="featured-project-content">
                      <div className="featured-project-text">
                        <p className="featured-project-description" dangerouslySetInnerHTML={{ __html: siftedFeaturedProject.description || '' }}></p>
                        <div className="project-keywords">
                          {siftedFeaturedProject.technologies.join(' · ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ResourceX and Growlink (Split in half) */}
              {previousProjects.filter(project => ['resourcex', 'growlink'].includes(project.id)).map((project) => (
                <div key={project.id} className="nested-grid-half">
                  <div className="featured-project-card">
                    <Link 
                      to={`/project/${project.id}`}
                      className="featured-project-image" 
                      data-project-id={project.id}
                      onMouseEnter={handleProjectMouseEnter}
                      onMouseMove={handleProjectMouseMove}
                      onMouseLeave={handleProjectMouseLeave}
                    >
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="project-image-primary"
                      />
                      {project.id === 'growlink' && (
                        <img 
                          src="/img/growlink hover.png"
                          alt={project.title}
                          className="project-image-hover"
                        />
                      )}
                      {project.id === 'resourcex' && (
                        <img 
                          src="/img/resourceX-2.png"
                          alt={project.title}
                          className="project-image-hover"
                        />
                      )}
                    </Link>
                    <div className="featured-project-content">
                      <div className="featured-project-text">
                        <p className="featured-project-description" dangerouslySetInnerHTML={{ __html: project.description || '' }}></p>
                        <div className="project-keywords">
                          {project.technologies.join(' · ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>
        </div>
      </section>

      {/* My Process Section */}
      <section id="my-process" className="section-title-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">My process</h2>
          </div>
          <div className="grid-column grid-span-6">
            <p style={{color: 'var(--color-text-primary)', margin: 0, fontSize: 'var(--font-size-lg)', textAlign: 'left'}}>I approach each project as a cycle of understanding, exploring, refining, and learning — moving quickly while staying grounded in user and business needs.</p>
          </div>
        </div>
      </section>

      <section className="section besides-work-section">
        <div className="grid-container">
          <div className="grid-column grid-span-6" style={{padding: '0'}}>
            <div className="nested-grid" style={{gridTemplateColumns: 'repeat(3, 1fr)'}}>
              <div className="nested-grid-third">
                <div className="side-project-card">
                  <h3 style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)', margin: '0 0 var(--space-4) 0'}}>1. Understand & Frame</h3>
                  <p className="side-project-description">
                    Start with context: who the users are, what's at stake, and which problems matter most.<br /><br />
                    Leverage fresh eyes when joining new domains to spot usability gaps others may overlook.
                  </p>
                </div>
              </div>
              
              <div className="nested-grid-third">
                <div className="side-project-card">
                  <h3 style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)', margin: '0 0 var(--space-4) 0'}}>2. Explore & Iterate</h3>
                  <p className="side-project-description">
                    Prototype early, from sketches to interactive flows, to test ideas with real users.<br /><br />
                    Collaborate closely with engineers, PMs, and cross-functional teams to align on scope and feasibility.
                  </p>
                </div>
              </div>

              <div className="nested-grid-third">
                {/* Empty space for first row, third column */}
              </div>

              <div className="nested-grid-third">
                {/* Empty space for second row, first column */}
              </div>

              <div className="nested-grid-third">
                <div className="side-project-card">
                  <h3 style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)', margin: '0 0 var(--space-4) 0'}}>3. Refine & Deliver</h3>
                  <p className="side-project-description">
                    Build scalable design patterns that make future work faster.<br /><br />
                    Partner with engineering through implementation to maintain quality and intent.
                  </p>
                </div>
              </div>

              <div className="nested-grid-third">
                <div className="side-project-card">
                  <h3 style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)', margin: '0 0 var(--space-4) 0'}}>4. Measure & Learn</h3>
                  <p className="side-project-description">
                    Track adoption, engagement, and business outcomes.<br /><br />
                    Use results to inform the next round of iteration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Design Values Section */}
      <section id="my-design-values" className="section-title-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">My design values</h2>
          </div>
        </div>
      </section>

      <section className="section besides-work-section">
        <div className="grid-container">
          <div className="grid-column grid-span-2">
            <p style={{fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)', margin: 0, textAlign: 'left'}}>Over the years, I've found a few principles that keep my work focused and impactful:</p>
          </div>
          
          <div className="grid-column grid-span-4">
            <div style={{fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)', lineHeight: 'var(--line-height-relaxed)', textAlign: 'left'}}>
              <p style={{margin: '0 0 var(--space-4) 0'}}><span style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)'}}>Solve the right problem first</span><br />
              <span style={{color: 'var(--color-text-secondary)'}}>There will always be many problems to fix; prioritization is the first design decision.</span></p>
              
              <p style={{margin: '0 0 var(--space-4) 0'}}><span style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)'}}>Insights over information</span><br />
              <span style={{color: 'var(--color-text-secondary)'}}>More data doesn't mean more clarity — design should highlight what matters so users can act with confidence.</span></p>
              
              <p style={{margin: '0'}}><span style={{fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-light)'}}>Trade-offs are opportunities</span><br />
              <span style={{color: 'var(--color-text-secondary)'}}>Balancing speed, scope, and user experience can push creativity rather than limit it.</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Product Design Section */}
      <section id="pre-product-design" className="section pre-product-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">Pre-product design</h2>
          </div>
          
          <div className="grid-column grid-span-2">
            <div className="architecture-card">
              <div className="architecture-image">
                <img 
                  src="/img/tcure-1.jpg" 
                  alt="T-Cure Bioscience Research Laboratory"
                  className="architecture-image-primary"
                />
                <img 
                  src="/img/tcure-2.jpg"
                  alt="T-Cure Bioscience Research Laboratory"
                  className="architecture-image-hover"
                />
              </div>
              <div className="architecture-content">
                <p className="architecture-description">
                  I converted a warehouse into a cancer research facility, designing collaborative circulation paths that encourage spontaneous innovation between formal lab spaces.
                </p>
                <div className="architecture-keywords">
                  <a href="https://bamcreative.io/portfolio/t-cure-bioscience/" target="_blank" rel="noopener noreferrer" className="architecture-link">
                    T-Cure Bioscience Research Laboratory
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid-column grid-span-2">
            <div className="architecture-card">
              <div className="architecture-image">
                <img 
                  src="/img/unionstation-1.jpg" 
                  alt="Los Angeles Union Station Lighting Retrofit"
                  className="architecture-image-primary"
                />
                <img 
                  src="/img/unionstation-2.jpg"
                  alt="Los Angeles Union Station Lighting Retrofit"
                  className="architecture-image-hover"
                />
              </div>
              <div className="architecture-content">
                <p className="architecture-description">
                  I contributed to the lighting retrofit of the historic 1939 transportation hub, focusing on energy efficiency through lighting coverage calculations and LED fixture selection while balancing preservation with modernization.
                </p>
                <div className="architecture-keywords">
                  <a href="https://bamcreative.io/bam-oscars-union-station-la/" target="_blank" rel="noopener noreferrer" className="architecture-link">
                    Los Angeles Union Station Lighting Retrofit
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid-column grid-span-2">
            <p className="section-description">
              I graduated with a degree in Architecture from USC and worked at several architectural design studios including <strong>BAM Creative</strong>, where I learned to think systematically about space, user experience, and the broader environmental context.
            </p>
          </div>
        </div>
      </section>

      {/* Outside of Work Title */}
      <section id="besides-work" className="section-title-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">Personal projects</h2>
          </div>
        </div>
      </section>

      {/* Outside of Work Section */}
      <section className="section besides-work-section">
        <div className="grid-container">
          <div className="grid-column grid-span-2 description-column">
            <p className="section-description">
              Outside of work, I enjoy taking photos, completing puzzles, and picking up new skills like data analytics, coding, and most recently, vibe coding with Claude Code.
            </p>
          </div>
          
          <div className="grid-column grid-span-4" style={{padding: '0'}}>
            <div className="nested-grid">
              {/* Featured 3D Visualization Project (Full width of nested grid) */}
              <div className="nested-grid-full">
                <div className="featured-side-project">
                  <div className="featured-side-project-card">
                    <a 
                      href="/3d-visualization" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="featured-side-project-image"
                      onMouseEnter={(e) => handleProjectMouseEnter(e, 'Try it')}
                      onMouseMove={handleProjectMouseMove}
                      onMouseLeave={handleProjectMouseLeave}
                    >
                      <img src="/img/virualizing-color.gif" alt="3D color visualization tool preview" />
                    </a>
                    <div className="featured-side-project-content">
                      <p className="side-project-description" style={{marginTop: '16px'}}>
                        I built an interactive web tool that converts 2D images into 3D point clouds mapped to RGB color space using Three.js and React with Cursor AI, exploring how spatial visualization can reveal hidden patterns in color data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Photography Project (Left half) */}
              <div className="nested-grid-half">
                <a href="https://www.flickr.com/photos/twttin/" target="_blank" rel="noopener noreferrer" className="side-project-card">
                  <div className="side-project-icon">📸</div>
                  <p className="side-project-description">
                    I explore visual storytelling through nature, architecture, people, and wildlife photography, sharpening my eye for composition and understanding how visual elements influence emotional response.
                  </p>
                  <div className="featured-project-cta">
                    <span className="cta-text">View more</span>
                    <IconArrowRight size={20} />
                  </div>
                </a>
              </div>
              
              {/* Flutter/Nature Project (Right half) */}
              <div className="nested-grid-half">
                <a href="https://x.com/NaturePortfolio/status/1560004323731439618" target="_blank" rel="noopener noreferrer" className="side-project-card">
                  <div className="side-project-icon">💻</div>
                  <p className="side-project-description">
                    I self-taught and developed a Flutter mobile app that contributed to biomedical research published in Nature Biomedical Engineering, bridging technical implementation with scientific research needs.
                  </p>
                  <div className="featured-project-cta">
                    <span className="cta-text">Learn more</span>
                    <IconArrowRight size={20} />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact-section">
        <div className="grid-container">
          <div className="section-title-row">
            <h2 className="section-title-text">Contact me</h2>
          </div>
          
          <div className="grid-column grid-span-6">
            <div className="contact-text">
              <p>I'm always interested in new opportunities and meaningful conversations. Email me at <a href="mailto:ziwang031@gmail.com" className="contact-link">ziwang031@gmail.com</a> or find me at <a href="https://www.linkedin.com/in/ziwang31" className="contact-link" target="_blank" rel="noopener noreferrer">LinkedIn</a>.</p>
              <p className="copyright-text">This site is handcrafted along with Claude Code. Copyright @ Zi Wang.</p>
            </div>
            <div className="contact-info" style={{marginTop: '80px'}}>
              <img 
                src="/img/selfie.jpg" 
                alt="Zi Wang" 
                className="contact-selfie" 
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Custom Tooltip */}
      {tooltip.show && (
        <div 
          className="custom-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y - 40,
            transform: 'translateX(-50%)'
          }}
        >
          My favorite selfie taken, using a tripod. @LA downtown
        </div>
      )}

      {/* Mouse-Following CTA */}
      {followingCta.show && (
        <div 
          className="mouse-following-cta"
          style={{
            left: followingCta.x,
            top: followingCta.y,
            transform: 'translate(-50%, -50%)'
          }}
        >
          {followingCta.text === 'Try it' ? (
            'Try it'
          ) : (
            <>Learn<br />more</>
          )}
        </div>
      )}
    </div>
  );
};

export default Home;